import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("prepify_token"));

  // Validate the stored token. IMPORTANT:
  //  - Only clear auth on explicit 401/403 (token is invalid/expired).
  //  - Transient failures (network, 500s, timeouts, backend cold-start) retry once
  //    and keep the token intact, so the user is NOT logged out on a tab reopen.
  const fetchUser = useCallback(async (authToken) => {
    const authHeader = { Authorization: `Bearer ${authToken}` };
    const tryOnce = () => axios.get(`${API}/auth/me`, { headers: authHeader, timeout: 10000 });
    try {
      const res = await tryOnce();
      setUser(res.data);
      return;
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        // Token genuinely invalid — logout
        console.warn("[auth] token rejected by server, logging out");
        localStorage.removeItem("prepify_token");
        delete axios.defaults.headers.common["Authorization"];
        setToken(null);
        setUser(null);
        return;
      }
      // Transient failure — retry once after a short wait
      try {
        await new Promise((r) => setTimeout(r, 800));
        const res = await tryOnce();
        setUser(res.data);
        return;
      } catch (err2) {
        const status2 = err2.response?.status;
        if (status2 === 401 || status2 === 403) {
          localStorage.removeItem("prepify_token");
          delete axios.defaults.headers.common["Authorization"];
          setToken(null);
          setUser(null);
        } else {
          // Still transient — keep the token so user isn't kicked out.
          // Leave user=null; ProtectedRoute will redirect to /login for now,
          // but reopening or manual refresh will re-attempt auth with the same token.
          console.error("[auth] /auth/me transient failure, keeping token:", err2?.message);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser(token).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  // JWT heartbeat: silently refresh the token every 24h so long-running sessions
  // don't hit the 7-day expiry mid-use. Only runs while authenticated.
  useEffect(() => {
    if (!token || !user) return;
    const REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours
    const refresh = async () => {
      try {
        const res = await axios.post(`${API}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        const { token: newToken } = res.data || {};
        if (newToken && newToken !== token) {
          localStorage.setItem("prepify_token", newToken);
          axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
          setToken(newToken);
        }
      } catch (err) {
        // Don't log the user out on transient refresh failures — the existing
        // token stays valid until its real expiry.
        console.warn("[auth] heartbeat refresh failed (non-fatal):", err?.response?.status || err?.message);
      }
    };
    const id = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [token, user]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("prepify_token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("prepify_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
